// src/utils/lossRunForm.js
//
// Pure helpers behind the Loss Runs log-entry form (FIG_LossRunRequests).
// No I/O here except saveLossRunRequest, which takes the write functions as an
// argument so the whole path is testable with mocks.
//
// Rules: blank is unknown — a field the user left blank is never written, and
// on edit only fields that changed are sent. Dates travel as "YYYY-MM-DD".
// FIG_LossRunRequests is a billing record: nothing here deletes.

import { dateOnlyOf, daysBetween } from './lossRunStatus';

export const LOSS_RUN_METHODS = ['Loss Run', 'No Known Loss Letter'];
export const LOSS_RUN_REQUESTS_LIST = 'FIG_LossRunRequests';

// Fields the form edits, in display order. Title / InsuredId / LossRunSourceId
// are set by the caller, never typed.
export const REQUEST_FORM_FIELDS = [
  'Method', 'TermsCovered', 'DateRequested', 'DateReceived', 'ValuedAsOf', 'VaultPath', 'MinutesSpent', 'Notes', 'PolicyIds',
];

// ── "Covers" checklist ↔ PolicyIds ────────────────────────────────────────────
// PolicyIds is a comma-separated list of FIG_Policies item ids; blank means
// every eligible policy of the source. The form shows a checklist of the
// source's policies, all checked by default.
export function policyIdsToSelected(policyIds, allIds) {
  const all = (allIds || []).map(String);
  const s = String(policyIds ?? '').trim();
  if (!s) return new Set(all);
  const listed = new Set(s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean));
  return new Set(all.filter(id => listed.has(id)));
}
// All checked -> blank (covers all). Otherwise the checked ids in list order.
// hadExplicit: the row already stores an explicit list; blank is never written,
// so "all checked" is then saved as the full explicit list (same meaning).
export function selectedToPolicyIds(selected, allIds, { hadExplicit = false } = {}) {
  const all = (allIds || []).map(String);
  const sel = new Set([...(selected || [])].map(String));
  const chosen = all.filter(id => sel.has(id));
  if (chosen.length === all.length && !hadExplicit) return '';
  return chosen.join(',');
}
const DATE_FIELDS = new Set(['DateRequested', 'DateReceived', 'ValuedAsOf']);

const blank = v => v === null || v === undefined || String(v).trim() === '';

export function emptyRequestForm() {
  return Object.fromEntries(REQUEST_FORM_FIELDS.map(k => [k, '']));
}

// A FIG_LossRunRequests row -> form strings. Dates keep their first 10 chars.
export function requestFormFromRow(row) {
  const f = emptyRequestForm();
  if (!row) return f;
  for (const k of REQUEST_FORM_FIELDS) {
    const v = row[k];
    if (v === null || v === undefined) continue;
    f[k] = DATE_FIELDS.has(k) ? (dateOnlyOf(v) || '') : String(v);
  }
  return f;
}

// "<Source name> <method> - <ValuedAsOf or DateRequested or today>"
export function buildRequestTitle(sourceName, form, today) {
  const date = dateOnlyOf(form?.ValuedAsOf) || dateOnlyOf(form?.DateRequested) || dateOnlyOf(today) || dateOnlyOf(new Date());
  const method = blank(form?.Method) ? 'request' : String(form.Method).trim();
  return `${String(sourceName || 'Source').trim()} ${method} - ${date}`;
}

// { field: 'plain-words message' }; empty object when valid.
export function validateRequestForm(form, today) {
  const errors = {};
  const f = form || {};
  if (blank(f.Method)) errors.Method = 'Pick a method.';
  else if (!LOSS_RUN_METHODS.includes(String(f.Method).trim())) errors.Method = 'Method must be Loss Run or No Known Loss Letter.';

  for (const k of DATE_FIELDS) {
    if (!blank(f[k]) && !dateOnlyOf(f[k])) errors[k] = 'Enter a date as YYYY-MM-DD.';
  }
  const req = dateOnlyOf(f.DateRequested);
  const rec = dateOnlyOf(f.DateReceived);
  const asOf = dateOnlyOf(f.ValuedAsOf);
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  if (req && rec && !errors.DateReceived && daysBetween(req, rec) < 0) {
    errors.DateReceived = 'Received date is before the requested date.';
  }
  if (asOf && !errors.ValuedAsOf && daysBetween(asOf, todayKey) < 0) {
    errors.ValuedAsOf = 'Valued-as-of date is in the future.';
  }
  if (!blank(f.MinutesSpent)) {
    const n = Number(String(f.MinutesSpent).trim());
    if (!Number.isFinite(n)) errors.MinutesSpent = 'Minutes must be a number.';
    else if (n < 0) errors.MinutesSpent = 'Minutes cannot be negative.';
  }
  return errors;
}

// Normalize one form value for comparison / writing: dates -> YYYY-MM-DD,
// minutes -> Number, text trimmed. Blank -> null (meaning "do not write").
function normalized(k, v) {
  if (blank(v)) return null;
  if (DATE_FIELDS.has(k)) return dateOnlyOf(v);
  if (k === 'MinutesSpent') return Number(String(v).trim());
  return String(v).trim();
}

// The fields to send. Create: Title + keys + every non-blank form field.
// Edit: only non-blank fields whose normalized value differs from the row.
// Title is generated on create only; an existing Title is left alone.
export function requestFieldsToWrite({ form, original, insuredId, sourceId, sourceName, today }) {
  const out = {};
  for (const k of REQUEST_FORM_FIELDS) {
    const v = normalized(k, form?.[k]);
    if (v === null) continue;                       // blank is never written
    if (original && normalized(k, original[k]) === v) continue; // unchanged
    out[k] = v;
  }
  if (!original) {
    out.Title = buildRequestTitle(sourceName, form, today);
    out.InsuredId = String(insuredId);
    out.LossRunSourceId = String(sourceId);
  }
  return out;
}

// { total, counted, missing } over the rows' MinutesSpent; blank is not zero.
export function minutesLogged(requests) {
  let total = 0, counted = 0, missing = 0;
  for (const r of requests || []) {
    const v = r?.MinutesSpent;
    if (blank(v) || !Number.isFinite(Number(v))) { missing++; continue; }
    total += Number(v); counted++;
  }
  return { total, counted, missing };
}

/**
 * Validate, build the payload, and write through the supplied api
 * ({ createItem, updateItem } with figApi's signatures). Returns
 *   { ok: false, errors }                   on validation failure
 *   { ok: true, skipped: true }             on an edit with nothing changed
 *   { ok: true, id, fields }                after a write
 */
export async function saveLossRunRequest({ token, form, original, insuredId, sourceId, sourceName, today, api, listName = LOSS_RUN_REQUESTS_LIST }) {
  const errors = validateRequestForm(form, today);
  if (Object.keys(errors).length) return { ok: false, errors };
  const fields = requestFieldsToWrite({ form, original, insuredId, sourceId, sourceName, today });
  if (original) {
    if (Object.keys(fields).length === 0) return { ok: true, skipped: true, id: original.id };
    await api.updateItem(token, listName, original.id, fields);
    return { ok: true, id: original.id, fields };
  }
  const created = await api.createItem(token, listName, fields);
  return { ok: true, id: created?.id, fields };
}
